
import React, { createContext, useState, useContext, useEffect } from 'react';

// Helper to decode JWT and get payload
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount and auto-logout if token expired
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const cleanToken = storedToken.replace(/\r?\n/g, '').trim();
        // Check token expiration
        const payload = parseJwt(cleanToken);
        if (payload && payload.exp) {
          const now = Math.floor(Date.now() / 1000);
          if (payload.exp < now) {
            // Token expired, auto-logout
            setUser(null);
            setToken(null);
            setIsAuthenticated(false);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            setLoading(false);
            return;
          }
        }
        setUser(userData);
        setToken(cleanToken);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error restoring auth:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Auto-logout if token expires while app is open
  useEffect(() => {
    if (!token) return;
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return;
    const now = Math.floor(Date.now() / 1000);
    const msUntilExpiry = (payload.exp - now) * 1000;
    if (msUntilExpiry <= 0) {
      logout();
      return;
    }
    const timeout = setTimeout(() => {
      logout();
    }, msUntilExpiry);
    return () => clearTimeout(timeout);
  }, [token]);

  const login = (userData, authToken) => {
    const cleanToken = authToken?.replace(/\r?\n/g, '').trim();
    setUser(userData);
    setToken(cleanToken);
    setIsAuthenticated(true);
    
    // Store in localStorage
    localStorage.setItem('auth_token', cleanToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    
    // Remove from localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  };

  const hasRole = (role) => {
    if (!user || !user.roles) return false;
    if (Array.isArray(role)) {
      return role.some(r => user.roles.includes(r));
    }
    return user.roles.includes(role);
  };

  const isAdmin = () => user?.roles?.includes('ADMIN') || false;
  const isHR = () => user?.roles?.includes('HR') || user?.roles?.includes('ADMIN') || false;
  const isRecruiter = () => user?.roles?.includes('RECRUITER') || user?.roles?.includes('ADMIN') || false;

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        token,
        loading,
        login,
        logout,
        setUser,
        hasRole,
        isAdmin,
        isHR,
        isRecruiter
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
